import { useState, useCallback } from "react";
import { Trash2 } from "lucide-react";
import { ContextMenuItem } from "@/dentlab/src/components/ui/context-menu";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogAction,
} from "@/dentlab/src/components/ui/alert-dialog";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { PreCheckDeleteJobResult } from "@/dentlab/src/types/delete-job.types";
import { DeleteJobConfirmDialog } from "./delete-job-confirm-dialog.component";

export function useDeleteJob(jobId: number, onDeleted?: () => void) {
	const [preCheckData, setPreCheckData] =
		useState<PreCheckDeleteJobResult | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [blockReasonOpen, setBlockReasonOpen] = useState(false);
	const [blockReason, setBlockReason] = useState("");

	const preCheckDeleteJob = useJobStore((state) => state.preCheckDeleteJob);

	const handleDeleteSelect = useCallback(async () => {
		const result = await preCheckDeleteJob(jobId);
		if (!result) {
			showNotification({
				message: "Fehler beim Prüfen der Löschbedingungen",
				type: "error",
			});
			return;
		}

		if (!result.can_delete) {
			setBlockReason(result.reason ?? "Löschen nicht möglich.");
			setBlockReasonOpen(true);
			return;
		}

		setPreCheckData(result);
		setConfirmOpen(true);
	}, [jobId, preCheckDeleteJob]);

	const hasActiveDialog = blockReasonOpen || preCheckData != null;
	const deleteDialogs = hasActiveDialog ? (
		<>
			<AlertDialog
				open={blockReasonOpen}
				onOpenChange={setBlockReasonOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Löschen nicht möglich
						</AlertDialogTitle>
						<AlertDialogDescription>
							{blockReason}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogAction>OK</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{preCheckData && (
				<DeleteJobConfirmDialog
					open={confirmOpen}
					onOpenChange={setConfirmOpen}
					jobId={jobId}
					preCheckData={preCheckData}
					onDeleted={onDeleted}
				/>
			)}
		</>
	) : null;

	return { handleDeleteSelect, deleteDialogs };
}

export const DeleteJobMenuTrigger: React.FC<{
	onSelect: () => void;
}> = ({ onSelect }) => {
	return (
		<ContextMenuItem
			variant="destructive"
			onSelect={onSelect}
			data-testid="job-context-menu-delete"
			aria-label="Auftrag endgültig löschen"
		>
			<Trash2 className="size-4" />
			Endgültig löschen
		</ContextMenuItem>
	);
};
